import { useEffect, useMemo, useReducer, useState } from 'react'
import { toast } from 'react-toastify'

import Button from '@material-ui/core/Button'
import InputAdornment from '@material-ui/core/InputAdornment'
import TextField from '@material-ui/core/TextField'
import SearchIcon from '@material-ui/icons/Search'

import MainContainer from '../../components/MainContainer'
import MainHeader from '../../components/MainHeader'
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper'

import { UserRound } from 'lucide-react'
import ClienteModal from '../../components/ClienteModal'
import ConfirmationModal from '../../components/ConfirmationModal'
import QuickTable from '../../components/QuickTable'
import toastError from '../../errors/toastError'
import api from '../../services/api'
import { i18n } from '../../translate/i18n'
import { useStyles } from './style'

// const reducer = (state, action) => {
//   if (action.type === 'LOAD_CLIENTES') {
//     const clientes = action.payload
//     const newClientes = []

//     clientes.forEach(cliente => {
//       const clienteIndex = state.findIndex(c => c.id === cliente.id)
//       if (clienteIndex !== -1) {
//         state[clienteIndex] = cliente
//       } else {
//         newClientes.push(cliente)
//       }
//     })

//     return [...state, ...newClientes]
//   }

//   if (action.type === 'UPDATE_CLIENTES') {
//     const cliente = action.payload
//     const clienteIndex = state.findIndex(c => c.id === cliente.id)

//     if (clienteIndex !== -1) {
//       state[clienteIndex] = cliente
//       return [...state]
//     } else {
//       return [cliente, ...state]
//     }
//   }

//   if (action.type === 'DELETE_CLIENTE') {
//     const clienteId = action.payload

//     const clienteIndex = state.findIndex(c => c.id === clienteId)
//     if (clienteIndex !== -1) {
//       state.splice(clienteIndex, 1)
//     }
//     return [...state]
//   }

//   if (action.type === 'RESET') {
//     return []
//   }
// }

const reducer = (state, action) => {
  switch (action.type) {
    case 'LOAD_CLIENTES': {
      const novosClientes = action.payload
      const idsExistentes = state.map(c => c.id)
      const filtrados = novosClientes.filter(c => !idsExistentes.includes(c.id))
      return [...state, ...filtrados]
    }
    case 'UPDATE_CLIENTES': {
      const clienteEditado = action.payload
      const index = state.findIndex(c => c.id === clienteEditado.id)

      if (index !== -1) {
        // Criamos um NOVO array com o cliente atualizado
        const novoEstado = [...state]
        novoEstado[index] = clienteEditado
        return novoEstado
      } else {
        return [clienteEditado, ...state]
      }
    }

    case 'DELETE_CLIENTE':
      return state.filter(c => c.id !== action.payload)

    case 'RESET':
      return []

    default:
      return state
  }
}

const Clientes = () => {
  const classes = useStyles()
  // const { user } = useContext(AuthContext)

  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [selectedCliente, setSelectedCliente] = useState(null)
  const [deletingCliente, setDeletingCliente] = useState(null)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [searchParam, setSearchParam] = useState('')
  const [clientes, dispatch] = useReducer(reducer, [])
  const [clienteModalOpen, setClienteModalOpen] = useState(false)

  const columns = useMemo(
    () => [
      {
        field: 'nome',
        headerName: i18n.t('clientes.table.name'),
        align: 'center'
      },
      {
        field: 'cpfCnpj',
        headerName: i18n.t('clientes.table.cpfCnpj'),
        align: 'center'
      },
      {
        field: 'tipo',
        headerName: i18n.t('clientes.table.tipo'),
        align: 'center'
      },
      {
        field: 'segmento',
        headerName: i18n.t('clientes.table.segmento'),
        align: 'center'
      }
    ],
    []
  )

  useEffect(() => {
    dispatch({ type: 'RESET' })
    setPageNumber(1)
  }, [searchParam])

  useEffect(() => {
    setLoading(true)
    const delayDebounceFn = setTimeout(() => {
      const fetchClientes = async () => {
        try {
          const { data } = await api.get('/clientes', {
            params: { searchParam, pageNumber }
          })
          dispatch({ type: 'LOAD_CLIENTES', payload: data.clientes })
          setHasMore(data.hasMore)
          setLoading(false)
        } catch (err) {
          toastError(err)
        }
      }
      fetchClientes()
    }, 500)
    return () => clearTimeout(delayDebounceFn)
  }, [searchParam, pageNumber])

  const handleOpenClienteModal = () => {
    setSelectedCliente(null)
    setClienteModalOpen(true)
  }

  const handleCloseClienteModal = () => {
    setSelectedCliente(null)
    setClienteModalOpen(false)
  }

  const handleSearch = event => {
    setSearchParam(event.target.value.toLowerCase())
  }

  const handleEditCliente = cliente => {
    setSelectedCliente(cliente)
    setClienteModalOpen(true)
  }

  const handleDeleteCliente = async clienteId => {
    try {
      await api.delete(`/clientes/${clienteId}`)
      toast.success(i18n.t('clientes.toasts.deleted'))
      dispatch({ type: 'DELETE_CLIENTE', payload: clienteId })
    } catch (err) {
      toastError(err)
    }
    setDeletingCliente(null)
    setSearchParam('')
    setPageNumber(1)
  }

  const handleClients = async () => {
    try {
      // Resetamos para a página 1 para garantir que o novo/editado apareça
      setPageNumber(1)
      dispatch({ type: 'RESET' })

      const { data } = await api.get('/clientes', {
        params: { searchParam, pageNumber: 1 }
      })

      dispatch({ type: 'LOAD_CLIENTES', payload: data.clientes })
      setHasMore(data.hasMore)
    } catch (err) {
      toastError(err)
    }
  }

  const loadMore = () => {
    setPageNumber(prevState => prevState + 1)
  }

  const handleScroll = e => {
    if (!hasMore || loading) return
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore()
    }
  }

  return (
    <MainContainer>
      <ClienteModal
        open={clienteModalOpen}
        onClose={handleCloseClienteModal}
        aria-labelledby="form-dialog-title"
        clienteId={selectedCliente && selectedCliente.id}
        onSave={handleClients}
      />
      <ConfirmationModal
        title={
          deletingCliente &&
          `${i18n.t('clientes.confirmationModal.deleteTitle')} ${
            deletingCliente.nome
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteCliente(deletingCliente.id)}
      >
        {i18n.t('clientes.confirmationModal.deleteMessage')}
      </ConfirmationModal>
      <MainHeader
        title={i18n.t('clientes.title')}
        description="Realize o cadastro e gerenciamento dos clientes."
        showTutorial
        // onTutorialClick={handleOpenTutorial}
      >
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t('clientes.searchPlaceholder')}
            type="search"
            variant="outlined"
            value={searchParam}
            onChange={handleSearch}
            className={classes.searchInput}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: 'gray' }} />
                </InputAdornment>
              )
            }}
          />
          <Button
            variant="contained"
            onClick={handleOpenClienteModal}
            className={classes.buttonNewClient}
            startIcon={<UserRound />}
          >
            Novo Cliente
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <QuickTable
        data={clientes}
        columns={columns}
        loading={loading}
        actions={['edit', 'delete']}
        onEdit={handleEditCliente}
        onDelete={cliente => {
          setDeletingCliente(cliente)
          setConfirmModalOpen(true)
        }}
        containerProps={{
          onScroll: handleScroll,
          className: classes.mainPaper
        }}
        // Tratamento para campos nulos ou lógicas específicas de exibição
        customCellRenderer={{
          segmento: item => item.segmento || '-'
        }}
      />
      {/* <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">
                {i18n.t('clientes.table.name')}
              </TableCell>
              <TableCell align="center">
                {i18n.t('clientes.table.cpfCnpj')}
              </TableCell>
              <TableCell align="center">
                {i18n.t('clientes.table.tipo')}
              </TableCell>
              <TableCell align="center">
                {i18n.t('clientes.table.segmento')}
              </TableCell>
              <TableCell align="center">
                {i18n.t('clientes.table.actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {clientes.map(cliente => (
                <TableRow key={cliente.id}>
                  <TableCell align="center">{cliente.nome}</TableCell>
                  <TableCell align="center">{cliente.cpfCnpj}</TableCell>
                  <TableCell align="center">{cliente.tipo}</TableCell>
                  <TableCell align="center">
                    {cliente.segmento || '-'}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleEditCliente(cliente)}
                    >
                      <EditIcon />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={e => {
                        setConfirmModalOpen(true)
                        setDeletingCliente(cliente)
                      }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={5} />}
            </>
          </TableBody>
        </Table>
      </Paper> */}
    </MainContainer>
  )
}

export default Clientes
